import React, { useState, useMemo, useEffect } from "react";
import { Card, Row, Col, Button, Form, Table, Alert, Badge, Modal } from "react-bootstrap";
import {
  FaUpload,
  FaCheckCircle,
  FaArrowRight,
  FaExchangeAlt,
  FaPlus,
  FaCheck,
  FaExclamationTriangle,
  FaInfoCircle
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import firebaseApp from "../../../firebase/credenciales";

const ConciliacionBancaria = ({
  movimientos = [],
  planillas = [],
  rucEmpresa = "",
  usuarioRegistro = "sistema",
  recargarMovimientos,
  onRegistrarMovimientoRapido, // We will call this if provided or handle internally
}) => {
  const [bankFile, setBankFile] = useState(null);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [conciliados, setConciliados] = useState({}); // bankTxId -> systemTxId
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Modal for quick registration of bank entries (e.g. fees, commissions)
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selectedBankTx, setSelectedBankTx] = useState(null);
  const [manualSelectBank, setManualSelectBank] = useState(null);
  const [manualSelectSystem, setManualSelectSystem] = useState(null);

  // Sync database reconciliation state with local state
  useEffect(() => {
    const initialConciliados = {};
    bankTransactions.forEach(tx => {
      if (tx.conciliadoSystemId) {
        initialConciliados[tx.id] = tx.conciliadoSystemId;
      }
    });
    if (Object.keys(initialConciliados).length > 0) {
      setConciliados(prev => ({ ...prev, ...initialConciliados }));
    }
  }, [bankTransactions]);

  const [quickForm, setQuickForm] = useState({
    descripcion: "",
    tercero: "BANCO",
    categoria: "GASTOS BANCARIOS",
    medioPago: "TRANSFERENCIA",
  });

  // Handle bank extract file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBankFile(file);
    setError("");
    setSuccess("");

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Parse raw array of arrays
        const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // Find header index (dynamic row matching looking for at least 3 matching headers)
        let headerIdx = -1;
        for (let i = 0; i < rawRows.length; i++) {
          const row = rawRows[i];
          if (Array.isArray(row)) {
            let matchCount = 0;
            row.forEach(cell => {
              const val = String(cell || "").toLowerCase();
              if (
                val.includes("fecha de operac") ||
                val.includes("fecha de proceso") ||
                val.includes("movimiento") ||
                val.includes("nro. de operac") ||
                val.includes("cargo") ||
                val.includes("abono") ||
                val.includes("saldo contable")
              ) {
                matchCount++;
              }
            });
            if (matchCount >= 3) {
              headerIdx = i;
              break;
            }
          }
        }

        let parsedTxs = [];

        if (headerIdx !== -1) {
          // Dynamic header mapping
          const headers = rawRows[headerIdx].map(h => String(h || "").trim().toLowerCase());
          const dataRows = rawRows.slice(headerIdx + 1);

          const fechaColIdx = headers.findIndex(h => h.includes("fecha de operac") || h === "fecha");
          const refColIdx = headers.findIndex(h => h.includes("nro. de operac") || h.includes("numero de operac") || h.includes("referencia"));
          const movColIdx = headers.findIndex(h => h === "movimiento" || h === "detalle");
          const descColIdx = headers.findIndex(h => h.includes("descripc"));
          const cargoColIdx = headers.findIndex(h => h === "cargo");
          const abonoColIdx = headers.findIndex(h => h === "abono");
          const montoColIdx = headers.findIndex(h => h === "monto" || h === "importe");

          parsedTxs = dataRows.map((row, idx) => {
            if (!row || row.length === 0 || !row[fechaColIdx]) return null;
            
            const rawFecha = String(row[fechaColIdx] || "").trim();
            if (!rawFecha || rawFecha.toLowerCase().includes("fecha") || rawFecha.toLowerCase().includes("rango")) return null;

            const descVal = String(row[descColIdx] || "").trim();
            const movVal = String(row[movColIdx] || "").trim();
            const desc = (movVal && descVal) ? `${movVal} - ${descVal}` : (descVal || movVal || "Operación Bancaria");
            const ref = String(row[refColIdx] || "").trim().replace("-", "");
            
            // Calculate amount
            let finalMonto = 0;
            if (montoColIdx !== -1 && row[montoColIdx] !== undefined && row[montoColIdx] !== null) {
              finalMonto = Number(row[montoColIdx]);
            } else {
              const cargo = row[cargoColIdx] !== undefined && row[cargoColIdx] !== null && String(row[cargoColIdx]).trim() !== "" ? Number(row[cargoColIdx]) : 0;
              const abono = row[abonoColIdx] !== undefined && row[abonoColIdx] !== null && String(row[abonoColIdx]).trim() !== "" ? Number(row[abonoColIdx]) : 0;
              
              if (cargo !== 0) {
                finalMonto = cargo < 0 ? cargo : -cargo;
              } else if (abono !== 0) {
                finalMonto = abono;
              }
            }

            if (isNaN(finalMonto) || finalMonto === 0) return null;

            // Convert Date from DD/MM/YYYY to YYYY-MM-DD for easier matching
            let formattedFecha = rawFecha;
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(rawFecha)) {
              const [dd, mm, yyyy] = rawFecha.split("/");
              formattedFecha = `${yyyy}-${mm}-${dd}`;
            }

            const bankTx = {
              id: `bank-${idx}`,
              fecha: formattedFecha,
              descripcion: desc,
              referencia: ref,
              monto: finalMonto,
              tipo: finalMonto > 0 ? "INGRESO" : "EGRESO",
            };

            // Cross-reference with database to see if already reconciled
            const yaExiste = movimientos.find(m =>
              m.conciliado &&
              m.conciliacion_banco_tx &&
              m.conciliacion_banco_tx.fecha === bankTx.fecha &&
              Math.round(Math.abs(m.conciliacion_banco_tx.monto)) === Math.round(Math.abs(bankTx.monto)) &&
              (m.conciliacion_banco_tx.referencia === bankTx.referencia || m.conciliacion_banco_tx.descripcion === bankTx.descripcion)
            );

            if (yaExiste) {
              bankTx.conciliadoSystemId = yaExiste.id;
            }

            return bankTx;
          }).filter(Boolean);
        } else {
          // Fallback parsing for standard flat JSON structure
          const json = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          parsedTxs = json.map((row, idx) => {
            const rawMonto = Number(row.Monto || row.monto || row.Importe || row.importe || 0);
            const rawCargo = Number(row.Cargo || row.cargo || 0);
            const rawAbono = Number(row.Abono || row.abono || 0);
            
            let finalMonto = rawMonto;
            if (finalMonto === 0) {
              if (rawCargo > 0) finalMonto = -rawCargo;
              if (rawAbono > 0) finalMonto = rawAbono;
            }

            const desc = String(row.Descripcion || row.descripcion || row.Detalle || row.detalle || "Operación Bancaria");
            const ref = String(row.Referencia || row.referencia || row.NumeroOperacion || row.operacion || "");
            const fechaStr = String(row.Fecha || row.fecha || new Date().toISOString().slice(0, 10));

            const bankTx = {
              id: `bank-${idx}`,
              fecha: fechaStr,
              descripcion: desc,
              referencia: ref,
              monto: finalMonto,
              tipo: finalMonto > 0 ? "INGRESO" : "EGRESO",
            };

            const yaExiste = movimientos.find(m =>
              m.conciliado &&
              m.conciliacion_banco_tx &&
              m.conciliacion_banco_tx.fecha === bankTx.fecha &&
              Math.round(Math.abs(m.conciliacion_banco_tx.monto)) === Math.round(Math.abs(bankTx.monto)) &&
              (m.conciliacion_banco_tx.referencia === bankTx.referencia || m.conciliacion_banco_tx.descripcion === bankTx.descripcion)
            );

            if (yaExiste) {
              bankTx.conciliadoSystemId = yaExiste.id;
            }

            return bankTx;
          });
        }

        if (parsedTxs.length === 0) {
          throw new Error("No se encontraron transacciones en el archivo cargado.");
        }

        setBankTransactions(parsedTxs);
        setSuccess(`Se cargaron ${parsedTxs.length} transacciones bancarias correctamente.`);
      } catch (err) {
        setError(err.message || "Error al procesar el archivo Excel del banco.");
      }
    };
    reader.readAsBinaryString(file);
  };

  // Unreconciled system transactions (both real database transactions & virtual planillas)
  const systemTransactions = useMemo(() => {
    const reconciledSystemIds = Object.values(conciliados);
    
    // Real system transactions
    const dbTransactions = movimientos.filter(m => !m.conciliado && !reconciledSystemIds.includes(m.id));
    
    // Virtual planilla transactions from the planillas collection
    const planillasMatchables = (planillas || [])
      .filter(p => p.estado === "APROBADO" || p.estado === "PROCESADO")
      .map(p => {
        // Reconciled check
        const isReconciled = movimientos.some(m => 
          (m.conciliado && (m.documentoRelacionado === `planilla-${p.id}` || (m.docTipo === "PLANILLA" && m.docNumero === p.periodo))) ||
          (reconciledSystemIds.includes(m.id) && m.documentoRelacionado === `planilla-${p.id}`)
        );

        if (isReconciled) return null;

        const ultimoDia = p.fecha_fin || `${p.periodo}-28`;

        return {
          id: `planilla-${p.id}`,
          fecha: ultimoDia,
          descripcion: `Planilla de Sueldos - Periodo ${p.periodo}`,
          tercero: "PLANILLA DE EMPLEADOS",
          categoria: "PLANILLA",
          tipo: "PAGO",
          monto: p.total_neto,
          isPlanilla: true,
          originalPlanilla: p
        };
      })
      .filter(Boolean);

    return [...dbTransactions, ...planillasMatchables];
  }, [movimientos, conciliados, planillas]);

  // Check if bank transaction can be registered directly (ITF, Retiro/Cargo socio, Planilla)
  const esPermitidoRegistrar = (tx) => {
    const desc = String(tx.descripcion || "").toUpperCase();
    const mov = String(tx.movimiento || "").toUpperCase();
    const fullText = `${mov} ${desc}`;
    return (
      fullText.includes("ITF") ||
      fullText.includes("RODRIGUEZ ARCE") ||
      fullText.includes("OSCAR CRISTH") ||
      fullText.includes("RETIRO EFECTIVO") ||
      fullText.includes("RETIRO CAJERO") ||
      fullText.includes("RETIRO DE SOCIO") ||
      fullText.includes("CARGO TRANSFERENCIA") ||
      fullText.includes("PLANILLA") ||
      fullText.includes("SUELDO") ||
      fullText.includes("REMUNERACION") ||
      fullText.includes("HABERES") ||
      fullText.includes("AFP")
    );
  };
  const autoMatches = useMemo(() => {
    const matches = {};
    bankTransactions.forEach(bankTx => {
      if (conciliados[bankTx.id]) return;

      const bankDate = new Date(bankTx.fecha);
      
      const potential = systemTransactions.find(sysTx => {
        const sysVal = Math.abs(Number(sysTx.monto));
        const bankVal = Math.abs(bankTx.monto);

        // Standard exact amount match
        let amtMatch = sysVal === bankVal;

        // Detraction match logic: If transaction in system is > 700, the bank deposit might be the net 88%
        if (!amtMatch && sysVal > 700) {
          const net88 = Number((sysVal * 0.88).toFixed(2));
          const detr12 = Number((sysVal * 0.12).toFixed(2));
          
          // Match bank deposit with either the 88% net or the 12% detraction voucher
          amtMatch = bankVal === net88 || bankVal === detr12;
        }
        
        // Date match (within +-5 days generally, or up to 15 days for payroll/planilla)
        const sysDate = new Date(sysTx.fecha);
        const diffTime = Math.abs(sysDate - bankDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const maxDiffDays = sysTx.isPlanilla ? 15 : 5;
        const dateMatch = diffDays <= maxDiffDays;

        // Reference text similarity
        const refMatch = bankTx.referencia && sysTx.referencia && 
          (bankTx.referencia.includes(sysTx.referencia) || sysTx.referencia.includes(bankTx.referencia));

        return amtMatch && (dateMatch || refMatch);
      });

      if (potential) {
        matches[bankTx.id] = potential;
      }
    });
    return matches;
  }, [bankTransactions, systemTransactions, conciliados]);

  // Perform quick match action
  const handleConciliar = async (bankTxId, systemTxId) => {
    setError("");
    setSuccess("");
    try {
      const bankTx = bankTransactions.find(x => x.id === bankTxId);
      if (!bankTx) return;

      const db = getFirestore(firebaseApp);

      // Check if this is a virtual planilla transaction match
      if (String(systemTxId).startsWith("planilla-")) {
        const planillaObj = systemTransactions.find(x => x.id === systemTxId);
        if (planillaObj && onRegistrarMovimientoRapido) {
          const payload = {
            rucEmpresa,
            usuarioRegistro,
            movimiento: {
              tipo: "PAGO",
              fecha: bankTx.fecha,
              descripcion: planillaObj.descripcion,
              tercero: planillaObj.tercero,
              categoria: planillaObj.categoria,
              medioPago: "TRANSFERENCIA",
              referencia: bankTx.referencia || planillaObj.originalPlanilla.periodo,
              moneda: "PEN",
              monto: Math.abs(bankTx.monto),
              documentoRelacionado: systemTxId,
              docTipo: "PLANILLA",
              docNumero: planillaObj.originalPlanilla.periodo
            }
          };

          const docId = await onRegistrarMovimientoRapido(payload);

          // Mark registered movement as reconciled
          await updateDoc(doc(db, "gte_movimientos", docId), {
            conciliado: true,
            fecha_conciliacion: new Date().toISOString(),
            conciliacion_banco_tx: {
              descripcion: bankTx.descripcion,
              referencia: bankTx.referencia,
              fecha: bankTx.fecha,
              monto: bankTx.monto
            }
          });

          setConciliados(prev => ({
            ...prev,
            [bankTxId]: docId
          }));
          setSuccess(`Pago de Planilla ${planillaObj.originalPlanilla.periodo} registrado y conciliado exitosamente.`);
          
          if (manualSelectBank === bankTxId) setManualSelectBank(null);
          if (manualSelectSystem === systemTxId) setManualSelectSystem(null);

          if (recargarMovimientos) {
            await recargarMovimientos();
          }
          return;
        }
      }

      // Normal database transaction match
      await updateDoc(doc(db, "gte_movimientos", systemTxId), {
        conciliado: true,
        fecha_conciliacion: new Date().toISOString(),
        conciliacion_banco_tx: {
          descripcion: bankTx.descripcion,
          referencia: bankTx.referencia,
          fecha: bankTx.fecha,
          monto: bankTx.monto
        }
      });

      setConciliados(prev => ({
        ...prev,
        [bankTxId]: systemTxId
      }));
      setSuccess("Transacciones conciliadas exitosamente y guardadas en la base de datos.");
      
      // Clear selections if any
      if (manualSelectBank === bankTxId) setManualSelectBank(null);
      if (manualSelectSystem === systemTxId) setManualSelectSystem(null);

      if (recargarMovimientos) {
        await recargarMovimientos();
      }
    } catch (err) {
      setError("No se pudo guardar la conciliación: " + err.message);
    }
  };

  // Perform manual match of selections
  const handleConciliarManual = () => {
    if (!manualSelectBank || !manualSelectSystem) return;
    handleConciliar(manualSelectBank, manualSelectSystem);
  };

  // Open modal to register a missing transaction from statement
  const handleOpenRegisterModal = (bankTx) => {
    setSelectedBankTx(bankTx);
    
    const desc = String(bankTx.descripcion || "").toUpperCase();
    const mov = String(bankTx.movimiento || "").toUpperCase();
    const fullText = `${mov} ${desc}`;

    let autoDesc = bankTx.descripcion || "Operación Bancaria";
    let autoTercero = "BANCO";
    let autoCategoria = bankTx.monto < 0 ? "GASTOS BANCARIOS" : "OTROS INGRESOS";

    if (fullText.includes("ITF")) {
      autoDesc = "ITF - Impuesto a las Transacciones Financieras";
      autoTercero = "SUNAT";
      autoCategoria = "TRIBUTOS / IMPUESTOS";
    } else if (
      fullText.includes("RODRIGUEZ ARCE OSCAR CRISTH") ||
      fullText.includes("OSCAR CRISTH") ||
      fullText.includes("RODRIGUEZ ARCE") ||
      fullText.includes("RETIRO EFECTIVO") ||
      fullText.includes("RETIRO CAJERO")
    ) {
      autoDesc = fullText.includes("RETIRO") 
        ? "Retiro de efectivo / Retiro de socio" 
        : "Transferencia a cuenta personal / Retiro de socio";
      autoTercero = "OSCAR CRISTH RODRIGUEZ ARCE";
      autoCategoria = "RETIRO DE SOCIO";
    } else if (fullText.includes("COMISION") || fullText.includes("COMISIÓ") || fullText.includes("MANTENIMIENTO") || fullText.includes("PORTES")) {
      autoDesc = `Comisión Bancaria - ${bankTx.descripcion}`;
      autoTercero = "BANCO";
      autoCategoria = "GASTOS BANCARIOS";
    }

    setQuickForm({
      fecha: bankTx.fecha,
      monto: Math.abs(bankTx.monto),
      descripcion: autoDesc,
      tercero: autoTercero,
      categoria: autoCategoria,
      medioPago: "TRANSFERENCIA",
    });
    setShowRegisterModal(true);
  };

  // Open modal for manual payment registration (without bank statement)
  const handleOpenManualRegisterModal = () => {
    setSelectedBankTx(null);
    setQuickForm({
      fecha: new Date().toISOString().slice(0, 10),
      monto: "",
      descripcion: "",
      tercero: "",
      categoria: "TRIBUTOS / IMPUESTOS",
      medioPago: "EFECTIVO",
    });
    setShowRegisterModal(true);
  };

  const handleRegisterQuickMovement = async () => {
    if (!rucEmpresa) return;

    try {
      let payload;
      if (selectedBankTx) {
        const isIngreso = selectedBankTx.monto > 0;
        payload = {
          rucEmpresa,
          usuarioRegistro,
          movimiento: {
            tipo: isIngreso ? "COBRO" : "PAGO",
            fecha: selectedBankTx.fecha,
            descripcion: quickForm.descripcion,
            tercero: quickForm.tercero,
            categoria: quickForm.categoria,
            medioPago: quickForm.medioPago,
            referencia: selectedBankTx.referencia || "CONCILIACION",
            moneda: "PEN",
            monto: Math.abs(selectedBankTx.monto),
            documentoRelacionado: null,
          }
        };
      } else {
        if (!quickForm.fecha || !quickForm.monto || !quickForm.descripcion) {
          setError("Por favor completa los campos obligatorios de fecha, monto y descripción.");
          return;
        }
        payload = {
          rucEmpresa,
          usuarioRegistro,
          movimiento: {
            tipo: "PAGO",
            fecha: quickForm.fecha,
            descripcion: quickForm.descripcion,
            tercero: quickForm.tercero || "SUNAT / BANCO",
            categoria: quickForm.categoria,
            medioPago: quickForm.medioPago || "EFECTIVO",
            referencia: "VOUCHER",
            moneda: "PEN",
            monto: Math.abs(Number(quickForm.monto)),
            documentoRelacionado: null,
          }
        };
      }

      if (onRegistrarMovimientoRapido) {
        const docId = await onRegistrarMovimientoRapido(payload);
        
        if (selectedBankTx) {
          // Mark as reconciled in database
          const db = getFirestore(firebaseApp);
          await updateDoc(doc(db, "gte_movimientos", docId), {
            conciliado: true,
            fecha_conciliacion: new Date().toISOString(),
            conciliacion_banco_tx: {
              descripcion: selectedBankTx.descripcion,
              referencia: selectedBankTx.referencia,
              fecha: selectedBankTx.fecha,
              monto: selectedBankTx.monto
            }
          });

          // Update local state
          setConciliados(prev => ({
            ...prev,
            [selectedBankTx.id]: docId
          }));
        }
      }
      
      setSuccess(selectedBankTx ? "Operación registrada en Tesorería y conciliada correctamente." : "Pago manual (voucher/efectivo) registrado correctamente.");
      setShowRegisterModal(false);
      
      if (recargarMovimientos) {
        await recargarMovimientos();
      }
    } catch (err) {
      setError("No se pudo registrar el movimiento: " + err.message);
    }
  };

  return (
    <div className="conciliacion-bancaria p-0">
      {/* File Upload Header */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={6}>
              <h5 className="fw-semibold mb-1">Cargar Extracto Bancario</h5>
              <p className="text-muted mb-0 small">
                Sube un archivo de cuenta (Excel/CSV) con columnas de Fecha, Detalle, Referencia e Importe para iniciar el auto-match.
              </p>
            </Col>
            <Col md={6} className="text-md-end mt-3 mt-md-0 d-flex justify-content-md-end gap-2 align-items-center">
              <div className="d-inline-block position-relative">
                <Form.Control
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileUpload}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    opacity: 0,
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                    zIndex: 2
                  }}
                />
                <Button variant="outline-primary" className="rounded-pill px-4 py-2 d-flex align-items-center gap-2">
                  <FaUpload /> {bankFile ? bankFile.name : "Subir Extracto"}
                </Button>
              </div>
              <Button
                variant="outline-success"
                className="rounded-pill px-4 py-2 d-flex align-items-center gap-2"
                onClick={handleOpenManualRegisterModal}
              >
                <FaPlus /> Registrar Pago Manual (Voucher/Efectivo)
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" className="border-0 shadow-sm mb-3">{error}</Alert>}
      {success && <Alert variant="success" className="border-0 shadow-sm mb-3">{success}</Alert>}

      {bankTransactions.length > 0 && (
        <Row className="g-4">
          {/* Column 1: Bank extract list */}
          <Col lg={6}>
            <Card className="border-0 shadow-lg" style={{ borderRadius: "16px", overflow: "hidden" }}>
              <Card.Header className="bg-primary text-white py-3 px-4 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold mb-0">1. Transacciones en Extracto Bancario</h6>
                <Badge bg="light" text="dark">{bankTransactions.filter(x => !conciliados[x.id]).length} Pendientes</Badge>
              </Card.Header>
              <Card.Body className="p-0" style={{ maxHeight: "650px", overflowY: "auto" }}>
                <Table hover responsive className="mb-0 align-middle small">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: "80px" }}>Fecha</th>
                      <th>Detalle / Operación</th>
                      <th className="text-end">Monto</th>
                      <th className="text-center" style={{ width: "120px" }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankTransactions.map(tx => {
                      const isConciliado = Boolean(conciliados[tx.id]);
                      const matchedSysTx = autoMatches[tx.id];
                      const isSelected = manualSelectBank === tx.id;

                      return (
                        <tr
                          key={tx.id}
                          className={isSelected ? "table-primary" : ""}
                          style={{
                            opacity: isConciliado ? 0.6 : 1,
                            backgroundColor: isConciliado ? "#f8fafc" : "transparent",
                            cursor: isConciliado ? "default" : "pointer"
                          }}
                          onClick={() => {
                            if (!isConciliado) setManualSelectBank(tx.id);
                          }}
                        >
                          <td className="py-3 px-2 text-muted">{tx.fecha}</td>
                          <td className="py-3">
                            <div className="fw-semibold text-dark">{tx.descripcion}</div>
                            {tx.referencia && <small className="text-muted d-block">Ref: {tx.referencia}</small>}
                            
                            {/* Visual tag for auto match recommendation */}
                            {!isConciliado && matchedSysTx && (
                              <Badge bg="success" className="mt-1 d-inline-flex align-items-center gap-1">
                                <FaCheck size={9} /> Match Sugerido: {matchedSysTx.tercero || matchedSysTx.descripcion}
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 text-end fw-bold">
                            <span className={tx.monto < 0 ? "text-danger" : "text-success"}>
                              S/ {tx.monto.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-3 text-center">
                            {isConciliado ? (
                              <Badge bg="success" className="rounded-pill p-2"><FaCheckCircle /> Reconciliado</Badge>
                            ) : matchedSysTx ? (
                              <Button
                                size="sm"
                                variant="success"
                                className="rounded-pill px-3 py-1 fw-bold"
                                style={{ fontSize: "0.75rem" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConciliar(tx.id, matchedSysTx.id);
                                }}
                              >
                                Conciliar
                              </Button>
                            ) : esPermitidoRegistrar(tx) ? (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                className="rounded-pill px-2 py-1"
                                style={{ fontSize: "0.7rem" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenRegisterModal(tx);
                                }}
                              >
                                <FaPlus size={8} className="me-1" /> Registrar
                              </Button>
                            ) : (
                              <span className="text-muted small d-block text-center" style={{ fontSize: "0.7rem" }}>
                                Reg. en Contabilidad
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          {/* Column 2: System Treasury registered transactions */}
          <Col lg={6}>
            <Card className="border-0 shadow-lg" style={{ borderRadius: "16px", overflow: "hidden" }}>
              <Card.Header className="bg-dark text-white py-3 px-4 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold mb-0">2. Movimientos Registrados en Tesorería</h6>
                <Badge bg="secondary">{systemTransactions.length} Libres</Badge>
              </Card.Header>
              <Card.Body className="p-0" style={{ maxHeight: "650px", overflowY: "auto" }}>
                <Table hover responsive className="mb-0 align-middle small">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: "80px" }}>Fecha</th>
                      <th>Tercero / Categoría</th>
                      <th className="text-end">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemTransactions.map(sysTx => {
                      const isSelected = manualSelectSystem === sysTx.id;
                      const isExpense = String(sysTx.tipo).toUpperCase() === "PAGO";
                      return (
                        <tr
                          key={sysTx.id}
                          className={isSelected ? "table-warning" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() => setManualSelectSystem(sysTx.id)}
                        >
                          <td className="py-3 px-2 text-muted">{sysTx.fecha}</td>
                          <td className="py-3">
                            <div className="fw-semibold text-dark">{sysTx.tercero || "Sin tercero"}</div>
                            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                              {sysTx.descripcion || sysTx.categoria}
                            </div>
                            {sysTx.referencia && <small className="text-muted d-block">Ref: {sysTx.referencia}</small>}
                          </td>
                          <td className="py-3 text-end fw-bold">
                            <span className={isExpense ? "text-danger" : "text-success"}>
                              S/ {Number(sysTx.monto).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Manual Matching Controls sticky bar */}
      {manualSelectBank && manualSelectSystem && (
        <Card className="fixed-bottom shadow-lg border-0 bg-light p-3 border-top" style={{ zIndex: 1040, left: 0, right: 0 }}>
          <div className="container-fluid d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-4">
              <div>
                <Badge bg="primary" className="me-2">Banco</Badge>
                <span className="fw-semibold text-dark">
                  {bankTransactions.find(x => x.id === manualSelectBank)?.descripcion} (S/ {bankTransactions.find(x => x.id === manualSelectBank)?.monto.toFixed(2)})
                </span>
              </div>
              <FaArrowRight className="text-muted" />
              <div>
                <Badge bg="dark" className="me-2">Sistema</Badge>
                <span className="fw-semibold text-dark">
                  {systemTransactions.find(x => x.id === manualSelectSystem)?.tercero || systemTransactions.find(x => x.id === manualSelectSystem)?.descripcion} (S/ {Number(systemTransactions.find(x => x.id === manualSelectSystem)?.monto).toFixed(2)})
                </span>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="rounded-pill px-3"
                onClick={() => {
                  setManualSelectBank(null);
                  setManualSelectSystem(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="rounded-pill px-4 fw-bold"
                onClick={handleConciliarManual}
              >
                Confirmar Conciliación Manual
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Quick Registration Modal */}
      <Modal show={showRegisterModal} onHide={() => setShowRegisterModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Registrar Operación Faltante</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="d-flex gap-2 align-items-center small">
            <FaInfoCircle />
            <span>
              {selectedBankTx 
                ? "Crea rápidamente esta transacción no registrada del extracto en el módulo de Tesorería."
                : "Registra manualmente un pago en tesorería sustentado por voucher, efectivo o depósitos directos."
              }
            </span>
          </Alert>

          {selectedBankTx ? (
            <div className="p-3 bg-light rounded mb-3 small">
              <div><strong>Fecha:</strong> {selectedBankTx.fecha}</div>
              <div><strong>Extracto Original:</strong> {selectedBankTx.descripcion}</div>
              <div><strong>Importe:</strong> <span className={selectedBankTx.monto < 0 ? "text-danger fw-bold" : "text-success fw-bold"}>S/ {selectedBankTx.monto.toFixed(2)}</span></div>
            </div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Fecha de Pago</Form.Label>
                <Form.Control
                  type="date"
                  value={quickForm.fecha || ""}
                  onChange={(e) => setQuickForm({ ...quickForm, fecha: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Monto (S/)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={quickForm.monto || ""}
                  onChange={(e) => setQuickForm({ ...quickForm, monto: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Medio de Pago</Form.Label>
                <Form.Select
                  value={quickForm.medioPago || "EFECTIVO"}
                  onChange={(e) => setQuickForm({ ...quickForm, medioPago: e.target.value })}
                >
                  <option value="EFECTIVO">EFECTIVO</option>
                  <option value="TRANSFERENCIA">TRANSFERENCIA</option>
                  <option value="DEPOSITO">DEPÓSITO BANCARIO</option>
                </Form.Select>
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Descripción del Movimiento</Form.Label>
            <Form.Control
              value={quickForm.descripcion}
              onChange={(e) => setQuickForm({ ...quickForm, descripcion: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tercero / Proveedor / Cliente</Form.Label>
            <Form.Control
              value={quickForm.tercero}
              onChange={(e) => setQuickForm({ ...quickForm, tercero: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Categoría de Gasto/Ingreso</Form.Label>
            <Form.Select
              value={quickForm.categoria}
              onChange={(e) => setQuickForm({ ...quickForm, categoria: e.target.value })}
            >
              <option value="GASTOS BANCARIOS">GASTOS BANCARIOS</option>
              <option value="RETIRO DE SOCIO">RETIRO DE SOCIO</option>
              <option value="TRIBUTOS / IMPUESTOS">TRIBUTOS / IMPUESTOS</option>
              <option value="COMPRAS">COMPRAS</option>
              <option value="VENTAS">VENTAS</option>
              <option value="OTROS INGRESOS">OTROS INGRESOS</option>
              <option value="PLANILLA">PLANILLA</option>
              <option value="SERVICIOS BÁSICOS">SERVICIOS BÁSICOS</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRegisterModal(false)}>Cerrar</Button>
          <Button variant="primary" onClick={handleRegisterQuickMovement}>Registrar y Guardar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ConciliacionBancaria;
